import React from 'react';
import { Users } from 'lucide-react';
import { Job } from '../../types/jobs';
import { JobCard } from './JobCard'; 
import { MailCheck, Bell, ExternalLink } from 'lucide-react';
import { openWebRedirect } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import * as amplitude from '@amplitude/analytics-browser';

interface JobListProps {
  jobs: Job[];
  isLoading: boolean;
  error: string | null;
  isPremium?: boolean;
  onJobClick: (job: Job) => void;
  categoryMap?: Record<string, string>;
}

export function JobList({ jobs, isLoading, error, isPremium = false, onJobClick, categoryMap = {} }: JobListProps) {
  const { userInfo } = useAuth();
  
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 auto-rows-fr">
        {[...Array(isPremium ? 4 : 9)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border ${isPremium ? 'border-amber-200 dark:border-amber-700/50' : 'border-gray-200/50 dark:border-gray-700/50'} p-4 sm:p-6`}>
              <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4 mb-3 sm:mb-4" />
              <div className="space-y-2 mb-3 sm:mb-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-2/3" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-full mb-3 sm:mb-4" />
              <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700/50">
                <div className="h-2 sm:h-3 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3" />
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <div className="mt-4">
          <a 
            href="https://pvtistes.net/jobs/index.php?do=notifications" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            <span>Voir les offres sur le forum</span>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 3h6v6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 14L21 3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 rounded-xl p-6 sm:p-8 border border-primary-100/50 dark:border-primary-800/30 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
          <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-white dark:bg-gray-800 rounded-full shadow-md flex items-center justify-center">
            <Bell className="w-8 h-8 sm:w-10 sm:h-10 text-primary-500 dark:text-primary-400" />
          </div>
          
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Recevez les offres d'emploi par email
            </h3>
            <p className="text-base text-gray-600 dark:text-gray-400 mb-6 max-w-2xl">
              Ne manquez aucune opportunité ! Activez les alertes emploi et recevez les nouvelles offres directement dans votre boîte mail dès leur publication.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <a 
                href="https://pvtistes.net/jobs/index.php?do=notifications" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02] cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  amplitude.track("Job Alert Signup Click From Empty State");
                  openWebRedirect("https://pvtistes.net/jobs/index.php?do=notifications", userInfo);
                }}
              >
                <MailCheck className="w-5 h-5" />
                <span className="font-medium">Activer les alertes emploi</span>
              </a>
              
              <a 
                href="https://pvtistes.net/jobs/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  amplitude.track("Job Forum Click From Empty State");
                  openWebRedirect("https://pvtistes.net/jobs/", userInfo);
                }}
              >
                <span>Voir les offres sur le forum</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
      {jobs.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          isPremium={isPremium}
          onClick={onJobClick}
          categoryMap={categoryMap}
        />
      ))}
    </div>
  );
}